import { useState, useEffect, useCallback } from 'react';
import {
  getAdminSeriesTemplates,
  uploadAdminTemplate,
  updateAdminTemplate,
  patchAdminTemplateWithFiles,
  publishAdminTemplate,
} from '../../api/admin';
import s from './AdminPages.module.css';

export default function AdminSeriesDetail({ seriesId, seriesName, onBack }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadTemplates = useCallback(() => {
    setLoading(true);
    getAdminSeriesTemplates(seriesId)
      .then((data) => setTemplates(data?.templates || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [seriesId]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  async function handlePublishToggle(template) {
    const newActive = !template.is_active;
    try {
      await publishAdminTemplate(template.id, newActive);
      setTemplates((prev) =>
        prev.map((t) => t.id === template.id ? { ...t, is_active: newActive, publish_at: null } : t)
      );
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div className={s.breadcrumb}>
          <button className={s.linkBtn} onClick={onBack}>← Library</button>
          <span className={s.breadcrumbSep}>/</span>
          <span className={s.breadcrumbCurrent}>{seriesName}</span>
        </div>
        <button className={s.primaryBtn} onClick={() => setShowUpload(!showUpload)}>
          {showUpload ? 'Cancel' : '+ Upload Episode'}
        </button>
      </div>

      {showUpload && (
        <UploadEpisodeForm
          seriesId={seriesId}
          onUploaded={(newTemplate) => {
            setShowUpload(false);
            setTemplates((prev) => [...prev, newTemplate].sort((a, b) => (a.episode_number || 0) - (b.episode_number || 0)));
          }}
          onCancel={() => setShowUpload(false)}
        />
      )}

      {error && <p className={s.errorText}>Error: {error}</p>}

      {loading ? (
        <p className={s.loadingText}>Loading episodes...</p>
      ) : templates.length === 0 ? (
        <div className={s.emptyHint}>No episodes yet. Upload the first one.</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>#</th>
                <th className={s.th}>Title</th>
                <th className={s.th}>Unlock Level</th>
                <th className={s.th}>Status</th>
                <th className={s.th}>Publish At</th>
                <th className={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                editingId === t.id
                  ? (
                    <EditEpisodeRow
                      key={t.id}
                      template={t}
                      onSaved={(updated) => {
                        setTemplates((prev) => prev.map((x) => x.id === updated.id ? updated : x));
                        setEditingId(null);
                      }}
                      onCancel={() => setEditingId(null)}
                    />
                  )
                  : (
                    <tr key={t.id}>
                      <td className={s.td}>
                        <span className={s.episodeNumBadge}>E{t.episode_number}</span>
                      </td>
                      <td className={s.td}>
                        <span className={s.storyTitle}>{t.title}</span>
                      </td>
                      <td className={s.td}>
                        {t.unlock_level_required === 0
                          ? <span className={s.badgeActive}>Free tier</span>
                          : <span className={s.levelBadge}>L{t.unlock_level_required}+</span>
                        }
                      </td>
                      <td className={s.td}>
                        <span className={t.is_active ? s.badgeActive : s.badgeDraft}>
                          {t.is_active ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className={s.td}>
                        {t.publish_at
                          ? new Date(t.publish_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className={s.td}>
                        <div className={s.actionGroup}>
                          <button
                            className={t.is_active ? s.warnBtn : s.primaryBtn}
                            onClick={() => handlePublishToggle(t)}
                          >
                            {t.is_active ? 'Unpublish' : 'Publish'}
                          </button>
                          <button
                            className={s.secondaryBtn}
                            onClick={() => setEditingId(t.id)}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UploadEpisodeForm({ seriesId, onUploaded, onCancel }) {
  const [title, setTitle] = useState('');
  const [episodeNumber, setEpisodeNumber] = useState('');
  const [unlockLevel, setUnlockLevel] = useState(0);
  const [publishAt, setPublishAt] = useState('');
  const [storyFile, setStoryFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    if (!episodeNumber) { setError('Episode number is required'); return; }
    if (!storyFile) { setError('Story file (.txt, .md, .docx, or .doc) is required'); return; }

    setSaving(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('series_id', seriesId);
    formData.append('episode_number', episodeNumber);
    formData.append('title', title.trim());
    formData.append('unlock_level_required', unlockLevel);
    if (publishAt) formData.append('publish_at', new Date(publishAt).toISOString());
    formData.append('file', storyFile);
    if (imageFile) formData.append('image', imageFile);

    try {
      const data = await uploadAdminTemplate(formData);
      setResult(data);
      onUploaded(data.template);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className={s.inlineForm} onSubmit={handleSubmit}>
      <h3 className={s.formTitle}>Upload Episode</h3>
      {error && <p className={s.errorText}>{error}</p>}
      {result && (
        <div className={s.uploadSuccess}>
          ✓ Uploaded — {result.curriculumCharsFound} curriculum chars detected, {result.pinyinGenerated} pinyin generated via fallback.
        </div>
      )}
      <div className={s.formRow}>
        <label className={s.label}>
          Episode # *
          <input
            className={s.input}
            type="number"
            min={1}
            value={episodeNumber}
            onChange={(e) => setEpisodeNumber(e.target.value)}
            placeholder="1"
            required
          />
        </label>
        <label className={s.label}>
          Unlock level (0 = free tier)
          <input
            className={s.input}
            type="number"
            min={0}
            max={12}
            value={unlockLevel}
            onChange={(e) => setUnlockLevel(e.target.value)}
          />
        </label>
      </div>
      <label className={s.label}>
        Title *
        <input
          className={s.input}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="The Lost Map"
          required
        />
      </label>
      <div className={s.formRow}>
        <label className={s.label}>
          Story file * (.txt, .md, .docx, or .doc)
          <input
            className={s.fileInput}
            type="file"
            accept=".txt,.md,.docx,.doc,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
            onChange={(e) => setStoryFile(e.target.files[0] || null)}
            required
          />
        </label>
        <label className={s.label}>
          Cover image (optional)
          <input
            className={s.fileInput}
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0] || null)}
          />
        </label>
      </div>
      <label className={s.label}>
        Scheduled publish date (optional — leave blank to publish manually)
        <input
          className={s.input}
          type="datetime-local"
          value={publishAt}
          onChange={(e) => setPublishAt(e.target.value)}
        />
      </label>
      <div className={s.formActions}>
        <button type="submit" className={s.primaryBtn} disabled={saving}>
          {saving ? 'Uploading…' : 'Upload Episode'}
        </button>
        <button type="button" className={s.secondaryBtn} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function EditEpisodeRow({ template, onSaved, onCancel }) {
  const [title, setTitle] = useState(template.title);
  const [episodeNumber, setEpisodeNumber] = useState(template.episode_number || '');
  const [unlockLevel, setUnlockLevel] = useState(template.unlock_level_required ?? 0);
  const [imageFile, setImageFile] = useState(null);
  const [storyFile, setStoryFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      let data;
      if (imageFile || storyFile) {
        const formData = new FormData();
        formData.append('title', title.trim());
        formData.append('episode_number', parseInt(episodeNumber, 10));
        formData.append('unlock_level_required', parseInt(unlockLevel, 10));
        if (imageFile) formData.append('image', imageFile);
        if (storyFile) formData.append('file', storyFile);
        data = await patchAdminTemplateWithFiles(template.id, formData);
      } else {
        data = await updateAdminTemplate(template.id, {
          title: title.trim(),
          episode_number: parseInt(episodeNumber, 10),
          unlock_level_required: parseInt(unlockLevel, 10),
        });
      }
      onSaved(data.template);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <tr>
      <td className={s.td}>
        <input
          className={s.inlineInput}
          type="number"
          min={1}
          value={episodeNumber}
          onChange={(e) => setEpisodeNumber(e.target.value)}
          style={{ width: 52 }}
        />
      </td>
      <td className={s.td}>
        <input
          className={s.inlineInput}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ width: '100%', minWidth: 180 }}
        />
        {error && <p className={s.errorText} style={{ marginTop: 4 }}>{error}</p>}
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className={s.label} style={{ fontSize: 11, marginBottom: 0 }}>
            Replace cover image (optional)
            <input
              className={s.fileInput}
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0] || null)}
            />
          </label>
          <label className={s.label} style={{ fontSize: 11, marginBottom: 0 }}>
            Replace story content (optional)
            <input
              className={s.fileInput}
              type="file"
              accept=".txt,.md,.docx,.doc,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              onChange={(e) => setStoryFile(e.target.files[0] || null)}
            />
          </label>
          {imageFile && <span style={{ fontSize: 11, color: '#666' }}>📷 {imageFile.name}</span>}
          {storyFile && <span style={{ fontSize: 11, color: '#666' }}>📄 {storyFile.name}</span>}
        </div>
      </td>
      <td className={s.td}>
        <input
          className={s.inlineInput}
          type="number"
          min={0}
          max={12}
          value={unlockLevel}
          onChange={(e) => setUnlockLevel(e.target.value)}
          style={{ width: 52 }}
        />
      </td>
      <td className={s.td} colSpan={3}>
        <div className={s.actionGroup}>
          <button className={s.primaryBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className={s.secondaryBtn} onClick={onCancel}>Cancel</button>
        </div>
      </td>
    </tr>
  );
}
